import { authedFetch, type StoredAuth } from "nazare-ui/utils";

export type SseMessage = {
  event: string;
  data: string;
  id?: string;
};

type SubscribeOptions = {
  baseUrl: string;
  auth: StoredAuth | null;
  path: string;
  onMessage: (message: SseMessage) => void;
  onConnected?: () => void;
  onError?: (error: Error) => void;
};

function parseFrame(frame: string): SseMessage | null {
  let event = "message";
  let id: string | undefined;
  const data: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    const raw = separator < 0 ? "" : line.slice(separator + 1);
    const value = raw.startsWith(" ") ? raw.slice(1) : raw;
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    else if (field === "id") id = value;
  }
  return data.length ? { event, data: data.join("\n"), ...(id ? { id } : {}) } : null;
}

/** Authenticated fetch-based SSE client with automatic reconnect and cleanup. */
export function subscribeAuthenticatedSse({
  baseUrl,
  auth,
  path,
  onMessage,
  onConnected,
  onError,
}: SubscribeOptions): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = async () => {
    controller = new AbortController();
    let failed = false;
    try {
      const response = await authedFetch(baseUrl, auth, path, {
        headers: { Accept: "text/event-stream" },
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`SSE request failed: ${response.status}`);
      }
      onConnected?.();
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!stopped) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const message = parseFrame(frame);
          if (message) onMessage(message);
        }
      }
    } catch (cause) {
      if (!stopped && !(cause instanceof DOMException && cause.name === "AbortError")) {
        failed = true;
        onError?.(cause instanceof Error ? cause : new Error(String(cause)));
      }
    }
    if (!stopped) {
      if (!failed) onError?.(new Error("SSE connection closed"));
      reconnectTimer = setTimeout(() => void connect(), 1_500);
    }
  };

  void connect();
  return () => {
    stopped = true;
    controller?.abort();
    if (reconnectTimer) clearTimeout(reconnectTimer);
  };
}
